import type { Chat, ChatMessage, ChatUser, ReactionKey } from './chat.types';
import type { TransitionEvent } from 'react';

import { ArrowLeftIcon, IconButton, Spinner, Tooltip, classNames } from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Message } from './message';
import { MessageForm } from './message-form';
import { MessagesList } from './messages-list';

import styles from './thread-view.module.css';

type Props = {
  isOpen: boolean;
  userId?: ChatUser['userId'];
  messages: ChatMessage[];
  threadMessageId: ChatMessage['messageId'] | null;
  loading?: boolean;
  error?: Chat['userActionError'];
  readOnly?: boolean;
  onReply: (parentMessageId: ChatMessage['messageId'], message: string) => Promise<ChatMessage | undefined>;
  onEditMessage: (messageId: ChatMessage['messageId'], message: ChatMessage['message']) => Promise<boolean>;
  onDeleteMessage: (message: ChatMessage) => void;
  onReactToMessage: (messageId: number, reactionName: ReactionKey) => void;
  onQuitThread: () => void;
};

export function ThreadView({
  isOpen,
  userId,
  messages,
  threadMessageId,
  loading,
  error,
  readOnly,
  onReply,
  onEditMessage,
  onDeleteMessage,
  onReactToMessage,
  onQuitThread,
}: Props) {
  const { t } = useTranslation();
  const [parentMessage, setParentMessage] = useState<ChatMessage>();
  const [threadedMessages, setThreadedMessages] = useState<ChatMessage[]>([]);
  const [messageValue, setMessageValue] = useState('');
  const [messageToEdit, setMessageToEdit] = useState<ChatMessage | null>(null);

  const replyCount = parentMessage?.threadInfo?.replyCount || 0;

  const saveEditedMessageData = (message: ChatMessage) => {
    setMessageToEdit(message);
    setMessageValue(message.message);
  };

  const clearMessageToEdit = () => {
    setMessageToEdit(null);
    setMessageValue('');
  };

  const handleTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || isOpen) return;

    clearMessageToEdit();
    setParentMessage(undefined);
    setThreadedMessages([]);
  };

  const handleSubmit = () => {
    if (messageToEdit) editMessage(messageToEdit.messageId);
    else replyToMessage();
  };

  const editMessage = async (messageId: number) => {
    if (await onEditMessage(messageId, messageValue)) {
      clearMessageToEdit();
    }
  };

  const replyToMessage = async () => {
    if (threadMessageId === null) return;

    const newMessage = await onReply(threadMessageId, messageValue);

    if (newMessage) {
      setThreadedMessages((messages) => [...messages, newMessage]);
      setMessageValue('');
    }
  };

  useEffect(() => {
    const message = messages.find((message) => message.messageId === threadMessageId);

    if (!message) return;

    const getMessages = async () => {
      const { threadedMessages } = await message.getThreadedMessagesByTimestamp(0, {
        prevResultSize: 100,
        nextResultSize: 100,
        isInclusive: true,
        includeParentMessageInfo: true,
      });

      setParentMessage(message);
      setThreadedMessages(threadedMessages as ChatMessage[]);
    };

    getMessages();
  }, [threadMessageId, messages]);

  const content = (
    <>
      <div className={styles.header}>
        <div className={styles.tooltipWrapper}>
          <Tooltip label={t('common.chat.closeThread')}>
            <IconButton
              rounded
              data-testid="chat-thread-close-button"
              onClick={onQuitThread}
              aria-label={t('common.chat.closeThread')}
            >
              <ArrowLeftIcon />
            </IconButton>
          </Tooltip>
        </div>

        {parentMessage && (
          <Message
            testId="chat-thread-parent-message"
            message={parentMessage}
            currentUserId={userId}
            hideReplyCount
            readOnly={readOnly}
            onEditMessage={saveEditedMessageData}
            onDeleteMessage={onDeleteMessage}
            onReactToMessage={onReactToMessage}
          />
        )}

        {replyCount > 0 && (
          <span data-testid="chat-thread-reply-count" className={styles.replyCount}>
            {t('common.chat.replies.count', { count: replyCount })}
          </span>
        )}
      </div>

      <MessagesList
        messages={threadedMessages}
        userId={userId}
        messageTestId="reply-message"
        readOnly={readOnly}
        onEditMessage={saveEditedMessageData}
        onDeleteMessage={onDeleteMessage}
        onReactToMessage={onReactToMessage}
      />

      {!readOnly && (
        <MessageForm
          testId="chat-thread-form"
          value={messageValue}
          onChange={setMessageValue}
          cancelButtonVisible={messageToEdit !== null}
          loading={loading}
          error={error}
          onCancel={clearMessageToEdit}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );

  return (
    <div
      data-testid="chat-thread"
      aria-hidden={!isOpen}
      className={classNames(styles.threadView, isOpen && styles.open)}
      onTransitionEnd={handleTransitionEnd}
    >
      {parentMessage ? content : <Spinner size={40} className={styles.spinner} />}
    </div>
  );
}
